import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Settings, CreditCard, HelpCircle, LogOut } from 'lucide-react';

/* ── Avatar gradient palette ──
   8 brand-appropriate gradient pairs using Orbis colors:
   Blues (#B8CCDF shades), Greens (#8FC4A0 shades), Amber/Copper (#D4B896 shades)
   Each user gets a deterministic gradient based on their user ID hash. */
const AVATAR_GRADIENTS = [
  ['#7BA7C9', '#B8CCDF'],  // Steel blue → Orbis blue
  ['#8FC4A0', '#C5E0CE'],  // Orbis green → Mint
  ['#D4B896', '#E8D5BD'],  // Orbis amber → Warm sand
  ['#7BA7C9', '#8FC4A0'],  // Steel blue → Green
  ['#A08B6E', '#D4B896'],  // Deep copper → Orbis amber
  ['#6B9DBF', '#A8C8B8'],  // Ocean blue → Sage green
  ['#C9A87C', '#8FC4A0'],  // Gold → Green
  ['#8AAEC2', '#D4B896'],  // Soft blue → Amber
];

function getAvatarGradient(userId) {
  if (!userId) return AVATAR_GRADIENTS[0];
  // Simple hash: sum char codes → modulo palette length
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

export function UserAvatar({ userId, name, size = 28, fontSize = 11 }) {
  const [c1, c2] = getAvatarGradient(userId);
  return (
    <div
      className="flex items-center justify-center shrink-0"
      style={{
        width: size, height: size, borderRadius: '50%',
        background: `linear-gradient(135deg, ${c1}, ${c2})`,
      }}
    >
      <span style={{ fontSize, fontWeight: 600, color: 'var(--orbis-bg)' }}>
        {name?.charAt(0)?.toUpperCase() || '?'}
      </span>
    </div>
  );
}

export default function UserMenu() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="cursor-pointer hover:ring-1 hover:ring-white/20 transition-all" style={{ borderRadius: '50%' }}>
          <UserAvatar userId={user.id} name={user.name} size={28} fontSize={11} />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56"
        style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-s2)', borderRadius: '12px' }}
      >
        <div className="px-3 py-3 flex items-center gap-3">
          <UserAvatar userId={user.id} name={user.name} size={36} fontSize={14} />
          <div className="min-w-0">
            <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--orbis-t1)' }} className="truncate">{user?.name}</p>
            <p style={{ fontSize: '11px', fontWeight: 400, color: 'var(--orbis-t4)' }} className="truncate">{user?.email}</p>
          </div>
        </div>
        <DropdownMenuSeparator style={{ background: 'var(--orbis-s2)' }} />
        <DropdownMenuItem
          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
          style={{ color: 'var(--orbis-t3)', fontSize: '13px', fontWeight: 400 }}
          onSelect={() => navigate('/settings')}
        >
          <Settings className="w-4 h-4" style={{ color: 'var(--orbis-t4)' }} />
          Account Settings
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
          style={{ color: 'var(--orbis-t3)', fontSize: '13px', fontWeight: 400 }}
          onSelect={() => navigate('/billing')}
        >
          <CreditCard className="w-4 h-4" style={{ color: 'var(--orbis-t4)' }} />
          Billing & Credits
        </DropdownMenuItem>
        <DropdownMenuSeparator style={{ background: 'var(--orbis-s2)' }} />
        <DropdownMenuItem
          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
          style={{ color: 'var(--orbis-t4)', fontSize: '13px', fontWeight: 400 }}
          onSelect={() => toast.info('Help center coming soon')}
        >
          <HelpCircle className="w-4 h-4" style={{ color: 'var(--orbis-t4)' }} />
          Help Center
        </DropdownMenuItem>
        <DropdownMenuSeparator style={{ background: 'var(--orbis-s2)' }} />
        <DropdownMenuItem
          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
          style={{ color: '#f87171', fontSize: '13px', fontWeight: 400 }}
          onSelect={logout}
        >
          <LogOut className="w-4 h-4" style={{ color: '#f87171' }} />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
